import React, { useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Flex,
  Button,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useToasts } from 'react-toast-notifications';
import DeleteDeviceModal from '../components/deleteDeviceModal.component';
import ModalComponent from '../components/adddevicemodal.component';
import AddDeviceGroupModal from '../components/addDeviceGroupModal.component';
import DeviceGroupEditModal from '../components/deviceGroupEditModal.component';
import DeviceGroupDeleteModal from '../components/deviceGroupDeleteModal.component';
import { useQuery } from 'react-query';
import ImportDeviceExcelModalComponent from '../components/ImportDeviceExcelModalComponent';
import { saveAs } from 'file-saver';
import {
  GetAllSides,
  deleteDeviceById,
  createDevice,
  updateDevice,
  getAllGroups,
  createDeviceGroup,
  updateDeviceGroup,
  deleteDeviceGroup,
  exportDeviceExcel,
} from '../api/side';
import { useDeviceSignalR } from '../hooks/useDeviceSignalR';

const Devices = () => {
  useDeviceSignalR();
  const { data, error, isLoading, refetch } = useQuery('dataKey', GetAllSides);
  const { data: groupData, refetch: refetchGroups } = useQuery(
    'groupKey',
    getAllGroups
  );
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddGroupModalOpen, setIsAddGroupModalOpen] = useState(false);
  const [isGroupEditModalOpen, setIsGroupEditModalOpen] = useState(false);
  const [isGroupDeleteModalOpen, setIsGroupDeleteModalOpen] = useState(false);
  const { addToast } = useToasts();

  const handleDelete = async (id, device) => {
    try {
      await deleteDeviceById(id, device);
      await refetch();
      addToast('Device deleted successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error(`Error deleting ${id} device:`, error);
      addToast(`Error deleting device: ${error.message}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedDevice(null);
    }
  };

  const handleSave = async deviceData => {
    try {
      if (selectedDevice) {
        await updateDevice(deviceData.id, deviceData);
        addToast('Device updated successfully', {
          appearance: 'success',
          autoDismiss: true,
        });
      } else {
        await createDevice(deviceData);
        addToast('Device added successfully', {
          appearance: 'success',
          autoDismiss: true,
        });
      }
      await refetch();
    } catch (error) {
      console.error('Error saving device:', error);
      addToast(`Error saving device: ${error.message}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsEditModalOpen(false);
      setSelectedDevice(null);
    }
  };

  const handleAddGroup = async name => {
    try {
      await createDeviceGroup(name); // Başarılı olursa refetch işlemi yapılacak
      addToast('Device group added successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
      await refetchGroups(); // Grupları yeniden yükle
    } catch (error) {
      console.error('Error adding device group:', error);
      addToast(`Error adding device group: ${error.message}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsAddGroupModalOpen(false);
    }
  };

  const handleExport = async () => {
    try {
      const data = await exportDeviceExcel();
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      saveAs(blob, 'devices_export.xlsx'); // İndirilen dosyanın adı
      addToast('File exported successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error('Error exporting device Excel:', error);
      addToast(`Error exporting file: ${error.message}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    }
  };
  const handleGroupEdit = async (id, newName) => {
    try {
      await updateDeviceGroup(id, newName); // ID ve yeni adı doğru şekilde gönderiyoruz
      addToast('Device group updated successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
      await refetchGroups(); // Grupları güncelliyoruz
    } catch (error) {
      console.error('Error updating device group:', error);
      addToast(`Error updating device group: ${error.message}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsGroupEditModalOpen(false);
      setSelectedGroup(null);
    }
  };

  // Device Group silme işlemi
  const handleGroupDelete = async id => {
    try {
      await deleteDeviceGroup(id);
      addToast('Device group deleted successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
      await refetchGroups();
    } catch (error) {
      console.error('Error deleting device group:', error);
      addToast(`Error deleting device group: ${error.message}`, {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsGroupDeleteModalOpen(false);
      setSelectedGroup(null);
    }
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>An error occurred: {error.message}</Text>
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      p={4}
    >
      {/* Device Table */}
      <Box flex="1" display="flex" flexDirection="column" w="100%">
        <TableContainer
          flex="1"
          maxHeight="calc(100vh - 320px)"
          overflowY="auto"
          w="100%"
        >
          <Table variant="simple" colorScheme="gray" size="sm" w="100%">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th>Device</Th>
                <Th>Description</Th>
                <Th>Connection Type</Th>
                <Th>Connection</Th>
                <Th>Enabled</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data && data.length > 0 ? (
                data.map(device => (
                  <Tr key={device.id} height="40px">
                    <Td>{device.deviceName}</Td>
                    <Td>{device.description}</Td>
                    <Td>{device.connType}</Td>
                    <Td>{device.statusStatement}</Td>
                    <Td>
                      <Badge colorScheme={device.isEnabled ? 'green' : 'red'}>
                        {device.isEnabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </Td>
                    <Td>
                      <Flex align="center">
                        <Text
                          as="span"
                          mr={2}
                          cursor="pointer"
                          onClick={() => {
                            setIsEditModalOpen(true);
                            setSelectedDevice(device);
                          }}
                        >
                          edit
                        </Text>
                        <Text as="span" mx={2}>
                          |
                        </Text>
                        <Text
                          as="span"
                          cursor="pointer"
                          onClick={() => {
                            setIsDeleteModalOpen(true);
                            setSelectedDevice(device);
                          }}
                        >
                          delete
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={6}>
                    <Text textAlign="center">No devices found.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
        <Box mt={3} mb={4}>
          <Flex justifyContent="flex-end" gap={2}>
            <Button size="sm" colorScheme="gray">
              Transfer
            </Button>
            <Button size="sm" colorScheme="gray" onClick={handleExport}>
              Export
            </Button>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() => setIsImportModalOpen(true)}
            >
              Import
            </Button>
            <Button
              onClick={() => {
                setIsEditModalOpen(true);
                setSelectedDevice(null);
              }}
              size="sm"
              colorScheme="gray"
            >
              Add Device
            </Button>
          </Flex>
        </Box>
      </Box>

      {/* Device Groups Table */}
      <Box flex="0.3" display="flex" flexDirection="column" w="100%">
        <TableContainer
          flex="1"
          maxHeight="calc(100vh * 0.3)"
          overflowY="auto"
          w="100%"
        >
          <Table variant="simple" colorScheme="gray" size="sm" w="100%">
            <Thead position="sticky" top={0} zIndex={1} bg="white">
              <Tr>
                <Th>Device Group</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {groupData && groupData.length > 0 ? (
                groupData.map(group => (
                  <Tr key={group.id}>
                    <Td>{group.name}</Td>
                    <Td>
                      <Flex align="center">
                        <Text
                          as="span"
                          mr={2}
                          cursor="pointer"
                          onClick={() => {
                            setIsGroupEditModalOpen(true);
                            setSelectedGroup(group);
                          }}
                        >
                          edit
                        </Text>
                        <Text as="span" mx={2}>
                          |
                        </Text>
                        <Text
                          as="span"
                          cursor="pointer"
                          onClick={() => {
                            setIsGroupDeleteModalOpen(true);
                            setSelectedGroup(group);
                          }}
                        >
                          delete
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={2}>
                    <Text textAlign="center">No device groups found.</Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
        <Button
          onClick={() => setIsAddGroupModalOpen(true)}
          mt={3}
          alignSelf="flex-end"
          size="sm"
          colorScheme="gray"
        >
          Add Device Group
        </Button>
      </Box>

      {/* Modals */}
      {isDeleteModalOpen && selectedDevice && (
        <DeleteDeviceModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onDelete={() => handleDelete(selectedDevice.id, selectedDevice)}
        />
      )}
      {isEditModalOpen && (
        <ModalComponent
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          side={selectedDevice}
          onSave={handleSave}
          refetch={refetch}
        />
      )}
      {isAddGroupModalOpen && (
        <AddDeviceGroupModal
          isOpen={isAddGroupModalOpen}
          onClose={() => setIsAddGroupModalOpen(false)}
          onSave={handleAddGroup}
        />
      )}
      {isGroupEditModalOpen && selectedGroup && (
        <DeviceGroupEditModal
          isOpen={isGroupEditModalOpen}
          onClose={() => setIsGroupEditModalOpen(false)}
          group={selectedGroup}
          onSave={handleGroupEdit}
        />
      )}
      {isGroupDeleteModalOpen && selectedGroup && (
        <DeviceGroupDeleteModal
          isOpen={isGroupDeleteModalOpen}
          onClose={() => setIsGroupDeleteModalOpen(false)}
          onDelete={() => handleGroupDelete(selectedGroup.id)}
        />
      )}
      <ImportDeviceExcelModalComponent
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={refetch} // Modal kapanınca cihazları yeniden yükle
      />
    </Box>
  );
};
export default Devices;
